import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, integrations, type IntegrationsStatus } from '../lib/api.js';
import { Card, PageHeader, SectionLabel, StatusPill } from '../components/ui.js';

export function IntegrationsPage() {
  const qc = useQueryClient();
  const status = useQuery<IntegrationsStatus, ApiError>({
    queryKey: ['integrations', 'status'],
    queryFn: integrations.status,
    staleTime: 15_000,
  });

  const banner = useOauthReturnBanner();

  if (status.isLoading) {
    return (
      <div className="space-y-12">
        <PageHeader eyebrow="Integrations" title="Connections" subtitle="Loading…" />
      </div>
    );
  }

  if (status.isError || !status.data) {
    return (
      <div className="space-y-12">
        <PageHeader
          eyebrow="Integrations"
          title="Connections"
          subtitle={`Failed to load: ${status.error?.message ?? 'unknown'}`}
        />
      </div>
    );
  }

  const data = status.data;

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Integrations"
        title="Connections"
        subtitle="Bloom needs read access to your Google surfaces so the engine can rank, post, and report. One-time per integration."
      />

      {banner && <ReturnBanner kind={banner.status} integration={banner.integration} reason={banner.reason} />}

      <section className="space-y-5">
        <SectionLabel>Google Business Profile</SectionLabel>
        <GbpCard data={data.gbp} onChanged={() => qc.invalidateQueries({ queryKey: ['integrations', 'status'] })} />
      </section>

      <section className="space-y-5">
        <SectionLabel>Search Console</SectionLabel>
        <GscCard data={data.gsc} onChanged={() => qc.invalidateQueries({ queryKey: ['integrations', 'status'] })} />
      </section>

      <section className="space-y-5">
        <SectionLabel>Google Analytics 4</SectionLabel>
        <Ga4Card
          data={data.ga4}
          serviceAccountEmail={data.ga4_service_account_email}
          onChanged={() => qc.invalidateQueries({ queryKey: ['integrations', 'status'] })}
        />
      </section>
    </div>
  );
}

// ── ?oauth_status banner ────────────────────────────────────────────

interface OauthBannerInfo {
  status: 'success' | 'failed';
  integration: string;
  reason: string;
}

function useOauthReturnBanner(): OauthBannerInfo | null {
  const [banner, setBanner] = useState<OauthBannerInfo | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('oauth_status');
    if (status !== 'success' && status !== 'failed') return;
    const integration = params.get('integration') ?? 'unknown';
    const reason = params.get('reason') ?? '';
    setBanner({ status, integration, reason });
    // Clean the URL so a refresh doesn't re-show the banner.
    params.delete('oauth_status');
    params.delete('integration');
    params.delete('reason');
    const cleaned = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', cleaned);
  }, []);
  return banner;
}

function ReturnBanner({
  kind,
  integration,
  reason,
}: {
  kind: 'success' | 'failed';
  integration: string;
  reason: string;
}) {
  const isOk = kind === 'success';
  const cls = isOk
    ? 'border-emerald-300/[0.22] bg-emerald-500/[0.05] text-emerald-100/95'
    : 'border-rose-300/[0.22] bg-rose-500/[0.05] text-rose-100/95';
  const label = isOk ? 'Connected' : 'Connection failed';
  const integrationLabel = integration === 'gbp'
    ? 'Google Business Profile'
    : integration === 'gsc'
      ? 'Search Console'
      : integration;
  return (
    <div className={`rounded-xl border px-4 py-3 ${cls}`}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-[0.22em]">{label}</span>
        <span className="text-sm">{integrationLabel}</span>
        {!isOk && reason && (
          <span className="text-xs text-rose-200/70 font-mono">{reason}</span>
        )}
      </div>
    </div>
  );
}

// ── GBP card ────────────────────────────────────────────────────────

function GbpCard({ data, onChanged }: { data: IntegrationsStatus['gbp']; onChanged: () => void }) {
  const start = useMutation({
    mutationFn: () => integrations.prepareGoogle('gbp'),
    onSuccess: ({ authorize_url }) => {
      window.location.assign(authorize_url);
    },
  });
  const disconnect = useMutation({
    mutationFn: () => integrations.disconnect('gbp'),
    onSuccess: onChanged,
  });
  return (
    <Card>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl text-cream">Google Business Profile</span>
            <StatusPill tone={data.connected ? 'ok' : 'neutral'}>
              {data.connected ? 'connected' : 'not connected'}
            </StatusPill>
          </div>
          <p className="text-sm text-cream/55 max-w-2xl">
            Read + write access for posting weekly updates, reading reviews, and managing location data.
          </p>
          {data.connected && (
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 mt-4 text-xs">
              <div>
                <dt className="text-cream/40 uppercase tracking-[0.18em] text-[10px]">Location</dt>
                <dd className="mt-1 font-mono text-cream/70 break-all">{data.location_id_remote}</dd>
              </div>
              <div>
                <dt className="text-cream/40 uppercase tracking-[0.18em] text-[10px]">Connected</dt>
                <dd className="mt-1 text-cream/70">{formatDate(data.oauth_completed_at)}</dd>
              </div>
            </dl>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!data.connected ? (
            <PrimaryButton
              onClick={() => start.mutate()}
              busy={start.isPending}
              error={start.error}
            >
              Connect
            </PrimaryButton>
          ) : (
            <SecondaryButton onClick={() => disconnect.mutate()} busy={disconnect.isPending}>
              Disconnect
            </SecondaryButton>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── GSC card ────────────────────────────────────────────────────────

function GscCard({ data, onChanged }: { data: IntegrationsStatus['gsc']; onChanged: () => void }) {
  const start = useMutation({
    mutationFn: () => integrations.prepareGoogle('gsc'),
    onSuccess: ({ authorize_url }) => {
      window.location.assign(authorize_url);
    },
  });
  const disconnect = useMutation({
    mutationFn: () => integrations.disconnect('gsc'),
    onSuccess: onChanged,
  });
  return (
    <Card>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl text-cream">Search Console</span>
            <StatusPill tone={data.connected ? 'ok' : 'neutral'}>
              {data.connected ? 'connected' : 'not connected'}
            </StatusPill>
          </div>
          <p className="text-sm text-cream/55 max-w-2xl">
            Read-only access to ranking and impression data. Used to score Bloom's lift over baseline.
          </p>
          {data.connected && (
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 mt-4 text-xs">
              <div>
                <dt className="text-cream/40 uppercase tracking-[0.18em] text-[10px]">Property</dt>
                <dd className="mt-1 font-mono text-cream/70 break-all">{data.property_url}</dd>
              </div>
              <div>
                <dt className="text-cream/40 uppercase tracking-[0.18em] text-[10px]">Connected</dt>
                <dd className="mt-1 text-cream/70">{formatDate(data.oauth_completed_at)}</dd>
              </div>
            </dl>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!data.connected ? (
            <PrimaryButton onClick={() => start.mutate()} busy={start.isPending} error={start.error}>
              Connect
            </PrimaryButton>
          ) : (
            <SecondaryButton onClick={() => disconnect.mutate()} busy={disconnect.isPending}>
              Disconnect
            </SecondaryButton>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── GA4 card (service account, not OAuth) ───────────────────────────

function Ga4Card({
  data,
  serviceAccountEmail,
  onChanged,
}: {
  data: IntegrationsStatus['ga4'];
  serviceAccountEmail: string | null;
  onChanged: () => void;
}) {
  const [propertyId, setPropertyId] = useState<string>(data.connected ? data.property_id : '');
  const [hint, setHint] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: () => integrations.connectGa4(propertyId.trim()),
    onSuccess: (result) => {
      onChanged();
      if (result.verified) {
        setHint(null);
      } else {
        setHint(result.hint ?? `${result.reason}: ${result.detail}`);
      }
    },
  });

  const disconnect = useMutation({
    mutationFn: () => integrations.disconnect('ga4'),
    onSuccess: () => {
      onChanged();
      setPropertyId('');
      setHint(null);
    },
  });

  const verifiedLabel = useMemo(() => {
    if (!data.connected) return 'not connected';
    return data.verified ? 'verified' : 'pending verify';
  }, [data]);
  const verifiedTone: 'ok' | 'warn' | 'neutral' = !data.connected
    ? 'neutral'
    : data.verified
      ? 'ok'
      : 'warn';

  return (
    <Card>
      <div className="space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-serif text-2xl text-cream">Google Analytics 4</span>
          <StatusPill tone={verifiedTone}>{verifiedLabel}</StatusPill>
        </div>
        <p className="text-sm text-cream/55 max-w-2xl">
          GA4 reads use a holocron-managed service account — paste your numeric Property ID and add the SA email below as a Viewer in your GA4 property.
        </p>

        {serviceAccountEmail ? (
          <div className="rounded-xl border border-cream/[0.08] bg-cream/[0.015] p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">
              Service account to add (Viewer role)
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono text-xs text-cream/85 break-all">{serviceAccountEmail}</code>
              <CopyButton value={serviceAccountEmail} />
            </div>
            <p className="text-xs text-cream/50">
              In GA4: Admin → Property Access Management → Add user → paste the email → Viewer role → Add.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-amber-300/[0.18] bg-amber-500/[0.04] p-4 text-sm text-amber-100/90">
            GA4 service account is not configured server-side yet. Ask your Bloom operator to set
            <code className="mx-1 font-mono text-xs text-amber-100">GA4_SERVICE_ACCOUNT_JSON_BASE64</code>
            on the API host.
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (/^[0-9]+$/.test(propertyId.trim())) connect.mutate();
          }}
          className="flex items-center gap-3 flex-wrap"
        >
          <label className="flex-1 min-w-[240px]">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-1">
              GA4 Property ID
            </span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g. 367851420"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className="w-full rounded-lg border border-cream/[0.12] bg-black/40 px-3 py-2 text-sm text-cream placeholder:text-cream/30 font-mono focus:outline-none focus:border-cream/30"
            />
          </label>
          <PrimaryButton
            type="submit"
            busy={connect.isPending}
            error={connect.error}
            disabled={!serviceAccountEmail || !/^[0-9]+$/.test(propertyId.trim())}
          >
            {data.connected ? 'Re-verify' : 'Connect'}
          </PrimaryButton>
          {data.connected && (
            <SecondaryButton onClick={() => disconnect.mutate()} busy={disconnect.isPending}>
              Disconnect
            </SecondaryButton>
          )}
        </form>

        {hint && (
          <div className="rounded-xl border border-amber-300/[0.18] bg-amber-500/[0.04] p-4 text-sm text-amber-100/90">
            {hint}
          </div>
        )}

        {data.connected && data.last_verify_error && !data.verified && (
          <div className="text-xs text-rose-200/70 font-mono">
            Last verify error: {data.last_verify_error}
          </div>
        )}

        {data.connected && data.verified && data.last_verified_at && (
          <div className="text-xs text-cream/50">
            Verified {formatDate(data.last_verified_at)}.
          </div>
        )}
      </div>
    </Card>
  );
}

// ── primitives ──────────────────────────────────────────────────────

function PrimaryButton({
  children,
  onClick,
  busy,
  error,
  type = 'button',
  disabled = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  busy?: boolean;
  error?: ApiError | Error | null;
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type={type}
        onClick={onClick}
        disabled={busy || disabled}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full bg-cream text-black hover:bg-cream/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? 'Working…' : children}
      </button>
      {error && (
        <span className="text-[11px] text-rose-300/80 font-mono">{error.message}</span>
      )}
    </div>
  );
}

function SecondaryButton({
  children,
  onClick,
  busy,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full bg-cream/[0.06] text-cream/85 hover:bg-cream/[0.12] border border-cream/[0.14] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-cream/[0.06] text-cream/70 hover:bg-cream/[0.12] border border-cream/[0.10] transition-colors"
    >
      {copied ? 'copied' : 'copy'}
    </button>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}
